import React, { useMemo, useState } from "react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from "recharts";
import { BarChart3, Users } from "lucide-react";

const fmt = (d, locale) => new Date(d).toLocaleDateString(locale, { day: "numeric", month: "short" });
const ORDER = ["sent", "seen", "clicks"];
const selCls = "border border-slate-200 rounded-full px-3 py-1 text-xs bg-white min-w-0";

function ChartCard({ icon: Icon, title, help, controls, empty, E, testid, children }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-3 sm:p-4 min-w-0" data-testid={testid}>
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div className="font-bold text-sm text-slate-800 inline-flex items-center gap-1.5"><Icon className="w-4 h-4 text-emerald-700" />{title}</div>
        <div className="flex items-center gap-1.5 flex-wrap">{controls}</div>
      </div>
      <div className="text-xs text-slate-500 mt-1 mb-2" data-testid={`${testid}-help`}>{help}</div>
      {empty ? <div className="rounded-xl bg-slate-50 text-center text-xs text-slate-500 py-6 px-3" data-testid={`${testid}-empty`}>{E.empty}</div> : <div className="h-48 w-full">{children}</div>}
    </div>
  );
}

const PinSelect = ({ pin, setPin, pins, S, testid }) => (
  <select value={pin} onChange={(e) => setPin(e.target.value)} className={selCls} aria-label="PIN" data-testid={testid}>
    <option value="all">{S.L.allPins}</option>
    {pins.map((p) => <option key={p.pincode} value={p.pincode}>{p.pincode}</option>)}
  </select>
);

const axis = { tick: { fontSize: 11, fill: "#64748b" }, tickLine: false, axisLine: false };

export function WeeklyActivity({ adId, rep, S }) {
  const E = S.eye;
  const [pin, setPin] = useState("all");
  const data = useMemo(() => {
    const m = {};
    rep.weekly.filter((w) => pin === "all" || w.pincode === pin).forEach((w) => {
      const r = (m[w.week_start] ||= { week: w.week_start, sent: 0, seen: 0, clicks: 0 });
      r.sent += w.impressions; r.seen += w.views || 0; r.clicks += w.clicks;
    });
    return Object.values(m).sort((a, b) => a.week.localeCompare(b.week)).map((r) => ({ ...r, label: fmt(r.week, S.locale) }));
  }, [rep.weekly, pin, S.locale]);
  return (
    <ChartCard icon={BarChart3} title={E.wkTitle} help={E.wkHelp} E={E} empty={!data.length} testid={`eye-weekly-${adId}`}
      controls={<PinSelect pin={pin} setPin={setPin} pins={rep.pins} S={S} testid={`eye-weekly-pin-${adId}`} />}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 6, right: 4, left: -20, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
          <XAxis dataKey="label" {...axis} minTickGap={12} interval="preserveStartEnd" />
          <YAxis allowDecimals={false} {...axis} width={40} />
          <Tooltip contentStyle={{ fontSize: 12, borderRadius: 12 }} />
          <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11 }} itemSorter={(i) => ORDER.indexOf(i.dataKey)} />
          <Bar dataKey="sent" name={E.m.delivered.t} fill="#94a3b8" radius={[4, 4, 0, 0]} maxBarSize={28} />
          <Bar dataKey="seen" name={E.m.views.t} fill="#047857" radius={[4, 4, 0, 0]} maxBarSize={28} />
          <Bar dataKey="clicks" name={E.m.clicks.t} fill="#f59e0b" radius={[4, 4, 0, 0]} maxBarSize={28} />
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}

// Genuine recorded distinct visitors per day / week; only periods with real events appear.
export function ReachChart({ adId, rep, S }) {
  const E = S.eye;
  const [mode, setMode] = useState("daily");
  const [pin, setPin] = useState("all");
  const data = useMemo(() => (rep.reach_trend?.[mode] || []).filter((r) => r.pincode === pin)
    .map((r) => ({ label: fmt(mode === "daily" ? r.date : r.week_start, S.locale), reach: r.unique_reach })), [rep.reach_trend, mode, pin, S.locale]);
  const tab = (m) => `px-3 py-1 text-xs font-semibold rounded-full ${mode === m ? "bg-emerald-700 text-white" : "text-slate-600"}`;
  return (
    <ChartCard icon={Users} title={`${E.rtTitle} · ${mode === "daily" ? S.rt.daily : S.rt.weekly}`} help={mode === "daily" ? E.rtDaily : E.rtWeekly} E={E} empty={!data.length} testid={`eye-reach-${adId}`}
      controls={<>
        <div className="inline-flex rounded-full border border-slate-200 p-0.5" role="tablist">
          <button type="button" className={tab("daily")} onClick={() => setMode("daily")} data-testid={`eye-reach-daily-${adId}`}>{S.rt.daily}</button>
          <button type="button" className={tab("weekly")} onClick={() => setMode("weekly")} data-testid={`eye-reach-weekly-${adId}`}>{S.rt.weekly}</button>
        </div>
        <PinSelect pin={pin} setPin={setPin} pins={rep.pins} S={S} testid={`eye-reach-pin-${adId}`} />
      </>}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 6, right: 4, left: -20, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
          <XAxis dataKey="label" {...axis} minTickGap={12} interval="preserveStartEnd" />
          <YAxis allowDecimals={false} {...axis} width={40} />
          <Tooltip contentStyle={{ fontSize: 12, borderRadius: 12 }} />
          <Bar dataKey="reach" name={E.rtTitle} fill={mode === "daily" ? "#0f766e" : "#065f46"} radius={[4, 4, 0, 0]} maxBarSize={32} />
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}
